import {
  DeviceReportFilters,
  matchesAssignedReport,
  matchesUnassignedReport,
  needsAssignedReports,
  needsUnassignedReports,
} from "@/data/models/DeviceReportFilters";
import {
  DeviceReportRow,
  sortRowsByReportedAt,
  toRowFromAssigned,
  toRowFromUnassigned,
} from "@/utils/DeviceReportDisplay";
import {
  getUnassignedDeviceReports,
  queryAssignedDeviceReports,
} from "@/data/repository/DeviceReportRepository";

/**
 * The one place that turns a filter set into rows, used by both the filter screen (for its count)
 * and the results list (for its rows).
 *
 * It exists because the two sources are read differently — attached reports come from an indexed
 * Firestore query, waiting ones from a Cloud Storage listing — and neither screen should have to
 * know that. Whatever the query could not express is applied here.
 */

export interface DeviceReportLoadResult {
  rows: DeviceReportRow[];
  /**
   * Whether the indexed side hit its page limit, so the count is a lower bound rather than a total.
   * The unassigned side is always read whole, being small by nature.
   */
  isPartial: boolean;
}

/**
 * Loads every report matching the filters.
 *
 * Both sources are read in parallel and only when the filters can actually match something in them:
 * asking for "nicht zugeordnet" does not touch Firestore, and narrowing by device category does not
 * touch Storage, because an unassigned report has no category to match.
 *
 * @param now Injected so a relative period ("last 12 months") is testable.
 */
export async function loadDeviceReportRows(
  filters: DeviceReportFilters,
  now: Date = new Date()
): Promise<DeviceReportLoadResult> {
  const [assigned, unassigned] = await Promise.all([
    needsAssignedReports(filters)
      ? queryAssignedDeviceReports(filters, now)
      : Promise.resolve({ reports: [], isPage: false }),
    needsUnassignedReports(filters)
      ? getUnassignedDeviceReports()
      : Promise.resolve([]),
  ]);

  const rows = [
    ...assigned.reports
      .filter((report) => matchesAssignedReport(report, filters, now))
      .map(toRowFromAssigned),
    ...unassigned
      .filter((report) => matchesUnassignedReport(report, filters, now))
      .map(toRowFromUnassigned),
  ];

  return { rows: sortRowsByReportedAt(rows), isPartial: assigned.isPage };
}
